/* ═══════════════════════════════════════════════════════════
 * AIS × 수프로 수목관리 시스템 — LH 데모(React, /demo2) 공통 목데이터
 * public/sian 정적 시안의 데이터를 그대로 이식하되, 지도 화면에서
 * 실제 Naver Map에 표시할 수 있도록 각 관리구역에 실좌표(lat/lng)를 추가함.
 * 좌표·폴리곤 값은 기존 /demo(demoStorage.ts)의 INITIAL_DEMO_SITES에
 * 등록된 실측 폴리곤(구역 경계) 10개를 그대로 재사용한다.
 * ═══════════════════════════════════════════════════════════ */

export interface LatLng {
    lat: number;
    lng: number;
}

/** 구역 weather 이모지 → public/images/weather/svg 실제 아이콘 경로 매핑 (지도 마커 표시용) */
export const WEATHER_ICON_SRC: Record<string, string> = {
    '☀️': '/images/weather/svg/clear-day.svg',
    '⛅': '/images/weather/svg/overcast-day.svg',
    '🌤️': '/images/weather/svg/partly-cloudy-day.svg',
    '🌧️': '/images/weather/svg/rain.svg',
};
export function weatherIconSrc(weather: string): string {
    return WEATHER_ICON_SRC[weather] ?? '/images/weather/svg/clear-day.svg';
}

/** 관리구역 상세패널 "현장사진" 슬라이드용 실제 조경수 식재 현장사진 */
export interface SitePhoto {
    src: string;
    caption: string;
}
export const SITE_PHOTOS: SitePhoto[] = [
    { src: '/images/site-photos/site-1.jpg', caption: '가로수 신규 식재 현장' },
    { src: '/images/site-photos/site-2.jpg', caption: '공원 산책로 조경수 식재' },
    { src: '/images/site-photos/site-3.jpg', caption: '조경수 지주목 설치 현장' },
    { src: '/images/site-photos/site-4.jpg', caption: '자작나무 조경 산책로' },
    { src: '/images/site-photos/site-5.jpg', caption: '조경수 이식 양생 단지' },
    { src: '/images/site-photos/site-6.jpg', caption: '신규 조경수 식재·멀칭 작업' },
];
/** 구역 id 기준으로 SITE_PHOTOS 중 일부(4장)를 순환 배정한다 */
export function zonePhotos(zoneId: string): SitePhoto[] {
    const seed = zoneId.charCodeAt(zoneId.length - 1) % SITE_PHOTOS.length;
    const n = 4;
    return Array.from(
        { length: n },
        (_, i) => SITE_PHOTOS[(seed + i) % SITE_PHOTOS.length],
    );
}

/** 작업 이력 화면 각 항목에 첨부할 실제 현장 작업사진 */
export const WORK_PHOTOS: SitePhoto[] = [
    { src: '/images/work-photos/w-1.jpg', caption: '도로변 가로수 관수 작업' },
    { src: '/images/work-photos/w-2.jpg', caption: '방제차량 약제 살포' },
    { src: '/images/work-photos/w-3.jpg', caption: '잔디광장 스프링클러 관수' },
    { src: '/images/work-photos/w-4.jpg', caption: '가로수 가지치기(전정) 작업' },
    { src: '/images/work-photos/w-5.webp', caption: '관목 예초·정지 작업' },
    { src: '/images/work-photos/w-6.jpg', caption: '신규 수목 이식 작업' },
    { src: '/images/work-photos/w-7.jpg', caption: '화단 조경수 시비 정비' },
    { src: '/images/work-photos/w-8.jpg', caption: '화단 관목 관수 작업' },
    { src: '/images/work-photos/w-9.jpg', caption: '수목 관수백 급수 작업' },
    { src: '/images/work-photos/w-10.jpg', caption: '조경수림 살수 작업' },
];
/** 작업 이력 인덱스 기준으로 WORK_PHOTOS를 순환 배정한다 */
export function workHistoryPhoto(index: number): SitePhoto {
    return WORK_PHOTOS[index % WORK_PHOTOS.length];
}

export type MoistStatus = 'good' | 'caution' | 'warn' | 'danger';

export function moistStatus(m: number): MoistStatus {
    if (m >= 70) return 'good';
    if (m >= 45) return 'caution';
    if (m >= 25) return 'warn';
    return 'danger';
}

export const STATUS_LABEL: Record<MoistStatus, string> = {
    good: '양호',
    caution: '주의',
    warn: '위험',
    danger: '심각',
};
export const STATUS_COLOR: Record<MoistStatus, string> = {
    good: '#1E8B53',
    caution: '#2563EB',
    warn: '#D97706',
    danger: '#DC2626',
};
export const STATUS_BG: Record<MoistStatus, string> = {
    good: '#D0F5E5',
    caution: '#DBEAFE',
    warn: '#FEF3C7',
    danger: '#FEE2E2',
};

export function treeEmoji(name: string): string {
    if (!name) return '🌳';
    if (/벚/.test(name)) return '🌸';
    if (/소나무|잣나무|반송/.test(name)) return '🌲';
    if (/메타세쿼이아|낙우송/.test(name)) return '🌲';
    if (/은행/.test(name)) return '🍃';
    if (/버들|수양/.test(name)) return '🌿';
    if (/느티|참나무|상수리/.test(name)) return '🌳';
    if (/이팝/.test(name)) return '🌼';
    if (/단풍/.test(name)) return '🍁';
    if (/목련|철쭉|영산홍/.test(name)) return '🌼';
    return '🌳';
}

/** Remix Icon 클래스 매핑 — public/growth 대시보드 수준의 파스텔 아이콘 배지에 사용 (이모지 대체) */
export function treeIconRi(name: string): string {
    if (!name) return 'ri-tree-line';
    if (/벚|이팝|목련|철쭉|영산홍/.test(name)) return 'ri-flower-line';
    if (/단풍|은행|버들|수양/.test(name)) return 'ri-leaf-line';
    return 'ri-tree-line';
}

export const TYPE_ICON_RI: Record<string, string> = {
    관개: 'ri-drop-line',
    전정: 'ri-scissors-line',
    방제: 'ri-flask-line',
    시비: 'ri-seedling-line',
    배수개선: 'ri-tools-line',
};

export const STATUS_ICON_RI: Record<MoistStatus, string> = {
    good: 'ri-checkbox-circle-line',
    caution: 'ri-alert-line',
    warn: 'ri-error-warning-line',
    danger: 'ri-alarm-warning-line',
};

/** Shoelace 공식 — 위경도 폴리곤의 대략적인 면적(㎡). 기존 /demo(demoStorage.ts)와 동일한 방식. */
export function calcPolygonAreaM2(pts: LatLng[]): number {
    if (pts.length < 3) return 0;
    const refLat = (pts[0].lat * Math.PI) / 180;
    const mPerLng = 111_320 * Math.cos(refLat);
    const mPerLat = 111_320;
    let area = 0;
    for (let i = 0; i < pts.length; i++) {
        const j = (i + 1) % pts.length;
        const xi = pts[i].lng * mPerLng,
            yi = pts[i].lat * mPerLat;
        const xj = pts[j].lng * mPerLng,
            yj = pts[j].lat * mPerLat;
        area += xi * yj - xj * yi;
    }
    return Math.abs(area / 2);
}

export function calcCentroid(pts: LatLng[]): LatLng {
    return {
        lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length,
        lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length,
    };
}

export type ZoneCategory = '공원' | '가로수' | '조경수';

/** 기획안 3장 "센서 전략" — 센서리스(AI 예측 중심, 초기도입/좁은면적 추천) vs 센서 기반(라이시미터 등 실측센서, 넓은면적/고정밀 추천) */
export type MonitorType = 'sensor' | 'sensorless';
export const MONITOR_LABEL: Record<MonitorType, string> = {
    sensor: '센서기반',
    sensorless: 'AI예측',
};
export const MONITOR_ICON: Record<MonitorType, string> = {
    sensor: '🛰️',
    sensorless: '🧠',
};
export const MONITOR_ICON_RI: Record<MonitorType, string> = {
    sensor: 'ri-radar-line',
    sensorless: 'ri-brain-line',
};

export interface FeedDay { day: string; ok: boolean; }
/** 구역별 일별 데이터 수신 현황(최근 N일) — 센서 기반은 실측 센서의 수신 로그,
 *  센서리스는 기상데이터 기반 AI 예측이 매일 자동 갱신되는 로그를 나타낸다. */
export function feedLog(zone: Zone, days = 10): FeedDay[] {
    const base = new Date('2026-07-20T00:00:00');
    const seed = zone.id.charCodeAt(zone.id.length - 1);
    return Array.from({ length: days }, (_, i) => {
        const d = new Date(base);
        d.setDate(d.getDate() - (days - 1 - i));
        const day = `${d.getMonth() + 1}/${d.getDate()}`;
        /* 센서기반 구역은 통신·배터리 이슈로 간헐적 결측이 발생할 수 있다(위례 센서 배터리
         * 부족 경고 AL-224와 같은 맥락). AI예측(센서리스)은 매일 자동 갱신되므로 결측이 없다. */
        const miss = zone.monitorType === 'sensor' && (i + seed) % 4 === 0;
        return { day, ok: !miss };
    });
}

/** 최근 데이터 수신(센서) 또는 예측 갱신(센서리스) 시각 */
export function lastFeedTime(zone: Zone): string {
    const seed = zone.id.charCodeAt(zone.id.length - 1);
    const hh = String(5 + (seed % 4)).padStart(2, '0');
    const mm = String((seed * 7) % 60).padStart(2, '0');
    return `2026-07-20 ${hh}:${mm}`;
}

export interface Zone {
    id: string;
    name: string;
    category: ZoneCategory;
    district: string;
    area: number;
    treeName: string;
    treeCount: number;
    moist: number;
    mgr: string;
    lastDate: string;
    temp: number;
    weather: string;
    ph: number;
    soilType: string;
    drainage: '양호' | '보통' | '불량';
    /** 센서 전략: 센서 기반(라이시미터 등) vs 센서리스(위성·기상데이터 AI 예측) */
    monitorType: MonitorType;
    /** 실제 Naver Map 표시용 중심좌표 (소속 폴리곤의 centroid) */
    lat: number;
    lng: number;
    /** 구역 경계 폴리곤 — 기존 /demo(demoStorage.ts INITIAL_DEMO_SITES) 실측값 재사용 */
    polygon: LatLng[];
    status: MoistStatus;
    icon: string;
}

const RAW_ZONES: Omit<Zone, 'status' | 'icon' | 'monitorType'>[] = [
    {
        id: 'Z01',
        name: '위례 신도시 중앙공원',
        category: '공원',
        district: '서울 송파구',
        area: 18400,
        treeName: '느티나무·왕벚나무',
        treeCount: 340,
        moist: 78,
        mgr: '김관리자',
        lastDate: '2026-07-19',
        temp: 27,
        weather: '☀️',
        ph: 6.6,
        soilType: '양토',
        drainage: '양호',
        lat: 37.958989,
        lng: 127.306121,
        polygon: [
            { lat: 37.9601257, lng: 127.3051527 },
            { lat: 37.9598973, lng: 127.3050991 },
            { lat: 37.9592883, lng: 127.3055926 },
            { lat: 37.9589837, lng: 127.3055175 },
            { lat: 37.9587469, lng: 127.3055282 },
            { lat: 37.9585439, lng: 127.3055497 },
            { lat: 37.9576049, lng: 127.3070196 },
            { lat: 37.9577064, lng: 127.3075345 },
            { lat: 37.9578502, lng: 127.3075238 },
            { lat: 37.9582224, lng: 127.3070303 },
            { lat: 37.9598973, lng: 127.3063651 },
            { lat: 37.9598297, lng: 127.3061183 },
            { lat: 37.9601596, lng: 127.305539 },
        ],
    },
    {
        id: 'Z02',
        name: '세종 다정동 가로수길',
        category: '가로수',
        district: '세종특별자치시',
        area: 6200,
        treeName: '이팝나무',
        treeCount: 128,
        moist: 55,
        mgr: '박현장',
        lastDate: '2026-07-16',
        temp: 29,
        weather: '⛅',
        ph: 6.3,
        soilType: '사질토',
        drainage: '보통',
        lat: 37.957234,
        lng: 127.303721,
        polygon: [
            { lat: 37.9580617, lng: 127.3029533 },
            { lat: 37.9579094, lng: 127.3036936 },
            { lat: 37.9579433, lng: 127.3041978 },
            { lat: 37.9571481, lng: 127.3044446 },
            { lat: 37.9571566, lng: 127.3046699 },
            { lat: 37.9568436, lng: 127.3049059 },
            { lat: 37.9565475, lng: 127.304863 },
            { lat: 37.9564714, lng: 127.3038652 },
            { lat: 37.9567928, lng: 127.3017517 },
            { lat: 37.9574611, lng: 127.3018697 },
        ],
    },
    {
        id: 'Z03',
        name: '화성 동탄2 호수공원',
        category: '공원',
        district: '경기 화성시',
        area: 24500,
        treeName: '메타세쿼이아·왕벚나무',
        treeCount: 410,
        moist: 32,
        mgr: '이담당',
        lastDate: '2026-07-11',
        temp: 30,
        weather: '☀️',
        ph: 6.1,
        soilType: '점토',
        drainage: '불량',
        lat: 37.955944,
        lng: 127.310015,
        polygon: [
            { lat: 37.9559892, lng: 127.3097983 },
            { lat: 37.9562007, lng: 127.310501 },
            { lat: 37.9559849, lng: 127.3105815 },
            { lat: 37.9556889, lng: 127.3096964 },
            { lat: 37.9558538, lng: 127.3094979 },
        ],
    },
    {
        id: 'Z04',
        name: '인천 검단신도시 조경수구역 A',
        category: '조경수',
        district: '인천 서구',
        area: 5100,
        treeName: '소나무·반송',
        treeCount: 96,
        moist: 18,
        mgr: '박현장',
        lastDate: '2026-07-08',
        temp: 28,
        weather: '🌤️',
        ph: 5.9,
        soilType: '사질토',
        drainage: '불량',
        lat: 37.956871,
        lng: 127.309789,
        polygon: [
            { lat: 37.9570381, lng: 127.309353 },
            { lat: 37.9569451, lng: 127.3097393 },
            { lat: 37.9569705, lng: 127.3101631 },
            { lat: 37.9567632, lng: 127.3103186 },
            { lat: 37.9566955, lng: 127.3098627 },
            { lat: 37.956814, lng: 127.3092994 },
        ],
    },
    {
        id: 'Z05',
        name: '하남 미사강변 가로수단지',
        category: '가로수',
        district: '경기 하남시',
        area: 4300,
        treeName: '느티나무',
        treeCount: 152,
        moist: 63,
        mgr: '김관리자',
        lastDate: '2026-07-17',
        temp: 24,
        weather: '🌧️',
        ph: 6.5,
        soilType: '양토',
        drainage: '양호',
        lat: 37.958398,
        lng: 127.310397,
        polygon: [
            { lat: 37.9577191, lng: 127.3092511 },
            { lat: 37.9580236, lng: 127.3107853 },
            { lat: 37.9577952, lng: 127.3108604 },
            { lat: 37.9576768, lng: 127.3110643 },
            { lat: 37.9576514, lng: 127.3114612 },
            { lat: 37.9582351, lng: 127.3125663 },
            { lat: 37.9584297, lng: 127.3124698 },
            { lat: 37.9583536, lng: 127.3122337 },
            { lat: 37.958675, lng: 127.3118368 },
            { lat: 37.9587681, lng: 127.3106137 },
            { lat: 37.9588357, lng: 127.3100236 },
            { lat: 37.9590895, lng: 127.309691 },
            { lat: 37.9589965, lng: 127.3095193 },
            { lat: 37.9591064, lng: 127.3089722 },
            { lat: 37.9587173, lng: 127.3087254 },
            { lat: 37.9585227, lng: 127.3088434 },
            { lat: 37.9585904, lng: 127.3093691 },
            { lat: 37.9584043, lng: 127.3102167 },
            { lat: 37.9582267, lng: 127.3102703 },
            { lat: 37.9581505, lng: 127.309176 },
        ],
    },
    {
        id: 'Z06',
        name: '파주 운정3 커뮤니티공원',
        category: '공원',
        district: '경기 파주시',
        area: 15800,
        treeName: '단풍나무·은행나무',
        treeCount: 268,
        moist: 71,
        mgr: '이담당',
        lastDate: '2026-07-18',
        temp: 26,
        weather: '☀️',
        ph: 6.4,
        soilType: '양토',
        drainage: '양호',
        lat: 37.955333,
        lng: 127.307459,
        polygon: [
            { lat: 37.9548387, lng: 127.3058823 },
            { lat: 37.9549994, lng: 127.3061988 },
            { lat: 37.9552532, lng: 127.3067245 },
            { lat: 37.9554266, lng: 127.3073146 },
            { lat: 37.9555746, lng: 127.3079315 },
            { lat: 37.9554689, lng: 127.3089346 },
            { lat: 37.9554393, lng: 127.3093262 },
            { lat: 37.955507, lng: 127.3093584 },
            { lat: 37.955562, lng: 127.3086235 },
            { lat: 37.9556254, lng: 127.3079368 },
            { lat: 37.9554901, lng: 127.3072234 },
            { lat: 37.9553335, lng: 127.3067191 },
            { lat: 37.9551728, lng: 127.3062953 },
            { lat: 37.9549656, lng: 127.3059574 },
        ],
    },
    {
        id: 'Z07',
        name: '남양주 다산신도시 조경수구역 B',
        category: '조경수',
        district: '경기 남양주시',
        area: 3900,
        treeName: '왕벚나무·이팝나무',
        treeCount: 84,
        moist: 44,
        mgr: '최현장',
        lastDate: '2026-07-14',
        temp: 27,
        weather: '⛅',
        ph: 6.2,
        soilType: '혼합',
        drainage: '보통',
        lat: 37.953379,
        lng: 127.318081,
        polygon: [
            { lat: 37.9538277, lng: 127.3177967 },
            { lat: 37.9534132, lng: 127.3173353 },
            { lat: 37.9528125, lng: 127.3184619 },
            { lat: 37.9534639, lng: 127.3187301 },
        ],
    },
    {
        id: 'Z08',
        name: '고양 향동지구 근린공원',
        category: '공원',
        district: '경기 고양시',
        area: 12100,
        treeName: '수양버들·느티나무',
        treeCount: 198,
        moist: 26,
        mgr: '박현장',
        lastDate: '2026-07-09',
        temp: 29,
        weather: '🌤️',
        ph: 6.0,
        soilType: '점토',
        drainage: '불량',
        lat: 37.954938,
        lng: 127.314318,
        polygon: [
            { lat: 37.9551474, lng: 127.3153183 },
            { lat: 37.9554097, lng: 127.3145458 },
            { lat: 37.9554097, lng: 127.313591 },
            { lat: 37.9550797, lng: 127.3137197 },
            { lat: 37.9548682, lng: 127.3139879 },
            { lat: 37.9547244, lng: 127.3139128 },
            { lat: 37.9543522, lng: 127.3145029 },
            { lat: 37.9545129, lng: 127.3149643 },
        ],
    },
    {
        id: 'Z09',
        name: '아산 탕정 가로수길',
        category: '가로수',
        district: '충남 아산시',
        area: 3400,
        treeName: '은행나무',
        treeCount: 112,
        moist: 82,
        mgr: '김관리자',
        lastDate: '2026-07-19',
        temp: 23,
        weather: '🌧️',
        ph: 6.6,
        soilType: '양토',
        drainage: '양호',
        lat: 37.952849,
        lng: 127.310855,
        polygon: [
            { lat: 37.954403, lng: 127.310383 },
            { lat: 37.9535654, lng: 127.3103079 },
            { lat: 37.9528717, lng: 127.3101041 },
            { lat: 37.9525756, lng: 127.3098037 },
            { lat: 37.9521526, lng: 127.3094282 },
            { lat: 37.9517466, lng: 127.3094282 },
            { lat: 37.9518904, lng: 127.3101792 },
            { lat: 37.951865, lng: 127.3106084 },
            { lat: 37.9519581, lng: 127.3108444 },
            { lat: 37.952491, lng: 127.3108551 },
            { lat: 37.9528125, lng: 127.3115847 },
            { lat: 37.9525249, lng: 127.3116812 },
            { lat: 37.9526687, lng: 127.3120031 },
            { lat: 37.9529986, lng: 127.3121104 },
            { lat: 37.9533117, lng: 127.3117993 },
            { lat: 37.9535908, lng: 127.3117563 },
            { lat: 37.9538869, lng: 127.3114774 },
            { lat: 37.9539631, lng: 127.3110268 },
        ],
    },
    {
        id: 'Z10',
        name: '부천 대장신도시 조경수구역 C',
        category: '조경수',
        district: '경기 부천시',
        area: 4700,
        treeName: '소나무·잣나무',
        treeCount: 74,
        moist: 50,
        mgr: '최현장',
        lastDate: '2026-07-15',
        temp: 28,
        weather: '⛅',
        ph: 6.3,
        soilType: '양토',
        drainage: '보통',
        lat: 37.953104,
        lng: 127.307495,
        polygon: [
            { lat: 37.9538023, lng: 127.3074219 },
            { lat: 37.9530579, lng: 127.3073146 },
            { lat: 37.9527364, lng: 127.3069605 },
            { lat: 37.9525249, lng: 127.3070893 },
            { lat: 37.9527279, lng: 127.3075399 },
            { lat: 37.9531509, lng: 127.308012 },
            { lat: 37.9537262, lng: 127.30813 },
        ],
    },
];

export const ZONES: Zone[] = RAW_ZONES.map(z => ({
    ...z,
    status: moistStatus(z.moist),
    icon: treeEmoji(z.treeName),
    /* 기존 데모(수분 모니터링 화면)와 동일한 배정: 면적이 넓은 공원(4곳)은 센서 기반, 나머지 6곳은 센서리스 */
    monitorType: (z.category === '공원'
        ? 'sensor'
        : 'sensorless') as MonitorType,
}));

export function findZone(id: string): Zone | undefined {
    return ZONES.find(z => z.id === id);
}

export interface Tree {
    id: string;
    zone: string;
    species: string;
    code: string;
    moist: number;
    height: number;
    girth: number;
    planted: string;
    lastDate: string;
    zoneName: string;
    district: string;
    status: MoistStatus;
    icon: string;
}

const RAW_TREES: Omit<Tree, 'zoneName' | 'district' | 'status' | 'icon'>[] = [
    {
        id: 'TR-1001',
        zone: 'Z01',
        species: '느티나무',
        code: 'A-14',
        moist: 81,
        height: 6.2,
        girth: 82,
        planted: '2021-04-12',
        lastDate: '2026-07-19',
    },
    {
        id: 'TR-1002',
        zone: 'Z01',
        species: '왕벚나무',
        code: 'A-27',
        moist: 74,
        height: 4.8,
        girth: 55,
        planted: '2022-03-20',
        lastDate: '2026-07-19',
    },
    {
        id: 'TR-1003',
        zone: 'Z01',
        species: '느티나무',
        code: 'A-33',
        moist: 69,
        height: 5.9,
        girth: 78,
        planted: '2021-04-12',
        lastDate: '2026-07-18',
    },
    {
        id: 'TR-1004',
        zone: 'Z02',
        species: '이팝나무',
        code: 'B-05',
        moist: 58,
        height: 4.1,
        girth: 40,
        planted: '2023-05-02',
        lastDate: '2026-07-16',
    },
    {
        id: 'TR-1005',
        zone: 'Z02',
        species: '이팝나무',
        code: 'B-12',
        moist: 49,
        height: 3.9,
        girth: 38,
        planted: '2023-05-02',
        lastDate: '2026-07-16',
    },
    {
        id: 'TR-1006',
        zone: 'Z02',
        species: '이팝나무',
        code: 'B-18',
        moist: 52,
        height: 4.0,
        girth: 39,
        planted: '2023-05-03',
        lastDate: '2026-07-15',
    },
    {
        id: 'TR-1007',
        zone: 'Z03',
        species: '메타세쿼이아',
        code: 'C-02',
        moist: 29,
        height: 8.4,
        girth: 95,
        planted: '2020-11-08',
        lastDate: '2026-07-11',
    },
    {
        id: 'TR-1008',
        zone: 'Z03',
        species: '왕벚나무',
        code: 'C-19',
        moist: 34,
        height: 4.5,
        girth: 48,
        planted: '2022-04-01',
        lastDate: '2026-07-11',
    },
    {
        id: 'TR-1009',
        zone: 'Z03',
        species: '메타세쿼이아',
        code: 'C-24',
        moist: 31,
        height: 8.1,
        girth: 91,
        planted: '2020-11-08',
        lastDate: '2026-07-10',
    },
    {
        id: 'TR-1010',
        zone: 'Z04',
        species: '소나무',
        code: 'D-03',
        moist: 16,
        height: 3.2,
        girth: 60,
        planted: '2019-10-15',
        lastDate: '2026-07-08',
    },
    {
        id: 'TR-1011',
        zone: 'Z04',
        species: '반송',
        code: 'D-07',
        moist: 20,
        height: 2.1,
        girth: 44,
        planted: '2019-10-15',
        lastDate: '2026-07-08',
    },
    {
        id: 'TR-1012',
        zone: 'Z04',
        species: '소나무',
        code: 'D-11',
        moist: 18,
        height: 3.4,
        girth: 62,
        planted: '2019-10-16',
        lastDate: '2026-07-07',
    },
    {
        id: 'TR-1013',
        zone: 'Z05',
        species: '느티나무',
        code: 'E-01',
        moist: 65,
        height: 5.5,
        girth: 70,
        planted: '2021-09-10',
        lastDate: '2026-07-17',
    },
    {
        id: 'TR-1014',
        zone: 'Z05',
        species: '느티나무',
        code: 'E-09',
        moist: 61,
        height: 5.3,
        girth: 68,
        planted: '2021-09-10',
        lastDate: '2026-07-17',
    },
    {
        id: 'TR-1015',
        zone: 'Z06',
        species: '은행나무',
        code: 'F-04',
        moist: 73,
        height: 4.9,
        girth: 52,
        planted: '2022-10-02',
        lastDate: '2026-07-18',
    },
    {
        id: 'TR-1016',
        zone: 'Z06',
        species: '단풍나무',
        code: 'F-15',
        moist: 69,
        height: 3.6,
        girth: 30,
        planted: '2023-03-14',
        lastDate: '2026-07-18',
    },
    {
        id: 'TR-1017',
        zone: 'Z07',
        species: '왕벚나무',
        code: 'G-02',
        moist: 46,
        height: 4.3,
        girth: 46,
        planted: '2022-04-20',
        lastDate: '2026-07-14',
    },
    {
        id: 'TR-1018',
        zone: 'Z07',
        species: '이팝나무',
        code: 'G-08',
        moist: 41,
        height: 4.0,
        girth: 39,
        planted: '2022-05-05',
        lastDate: '2026-07-13',
    },
    {
        id: 'TR-1019',
        zone: 'Z08',
        species: '수양버들',
        code: 'H-03',
        moist: 24,
        height: 6.8,
        girth: 88,
        planted: '2020-06-18',
        lastDate: '2026-07-09',
    },
    {
        id: 'TR-1020',
        zone: 'Z08',
        species: '느티나무',
        code: 'H-11',
        moist: 28,
        height: 5.7,
        girth: 74,
        planted: '2021-04-30',
        lastDate: '2026-07-09',
    },
    {
        id: 'TR-1021',
        zone: 'Z09',
        species: '은행나무',
        code: 'I-06',
        moist: 85,
        height: 5.0,
        girth: 54,
        planted: '2021-11-11',
        lastDate: '2026-07-19',
    },
    {
        id: 'TR-1022',
        zone: 'Z09',
        species: '은행나무',
        code: 'I-14',
        moist: 79,
        height: 4.8,
        girth: 51,
        planted: '2021-11-11',
        lastDate: '2026-07-19',
    },
    {
        id: 'TR-1023',
        zone: 'Z10',
        species: '소나무',
        code: 'J-02',
        moist: 52,
        height: 3.8,
        girth: 58,
        planted: '2022-02-25',
        lastDate: '2026-07-15',
    },
    {
        id: 'TR-1024',
        zone: 'Z10',
        species: '잣나무',
        code: 'J-09',
        moist: 48,
        height: 3.5,
        girth: 52,
        planted: '2022-02-25',
        lastDate: '2026-07-14',
    },
];

export const TREES: Tree[] = RAW_TREES.map(t => {
    const z = findZone(t.zone);
    return {
        ...t,
        zoneName: z ? z.name : '-',
        district: z ? z.district : '-',
        status: moistStatus(t.moist),
        icon: treeEmoji(t.species),
    };
});

export function findTree(id: string): Tree | undefined {
    return TREES.find(t => t.id === id);
}

export interface TreeDataBadge { text: string; icoRi: string; tone: MonitorType; }
/** 수목 목록·상세 화면에서 구역의 데이터 방식(센서기반 / AI예측)을 그대로 물려받아 표시하기
 *  위한 배지 정보 — 구역 단위 MONITOR_LABEL/MONITOR_ICON_RI와 동일한 용어를 사용한다. */
export function treeDataBadge(tree: Tree): TreeDataBadge {
    const type: MonitorType = findZone(tree.zone)?.monitorType ?? 'sensorless';
    return { text: MONITOR_LABEL[type], icoRi: MONITOR_ICON_RI[type], tone: type };
}

export type AlertLevel = 'danger' | 'warn' | 'caution';

export interface AlertItem {
    id: string;
    zone: string;
    level: AlertLevel;
    title: string;
    msg: string;
    time: string;
    read: boolean;
    zoneName: string;
}

const RAW_ALERTS: Omit<AlertItem, 'zoneName'>[] = [
    {
        id: 'AL-230',
        zone: 'Z08',
        level: 'warn',
        title: '고양 향동지구 근린공원 수분율 주의 구간 진입(26%)',
        msg: '수양버들·느티나무 198주 대상 구역의 수분율이 위험 임계치에 근접했습니다. 2일 이내 관개를 권장합니다.',
        time: '2026-07-20 07:40',
        read: false,
    },
    {
        id: 'AL-231',
        zone: 'Z04',
        level: 'danger',
        title: '토양 수분율 위험 수준(18%) — 즉시 관개 필요',
        msg: '인천 검단신도시 조경수구역 A의 소나무·반송 96주가 3일 연속 위험 수준입니다. 72시간 이내 미조치 시 고사 위험이 있습니다.',
        time: '2026-07-20 08:12',
        read: false,
    },
    {
        id: 'AL-229',
        zone: 'Z03',
        level: 'warn',
        title: '화성 동탄2 호수공원 배수 불량 + 수분 위험 중복 경보',
        msg: '토양 배수 상태가 불량으로 확인되어 관개 후에도 효과가 낮을 수 있습니다. 배수 개선 작업 병행을 권장합니다.',
        time: '2026-07-19 17:22',
        read: true,
    },
    {
        id: 'AL-228',
        zone: 'Z07',
        level: 'caution',
        title: '남양주 다산신도시 조경수구역 B 수분율 주의(44%)',
        msg: '왕벚나무·이팝나무 84주 대상 수분율이 주의 구간입니다. 정기 점검 일정을 앞당기는 것을 권장합니다.',
        time: '2026-07-19 14:05',
        read: true,
    },
    {
        id: 'AL-227',
        zone: 'Z04',
        level: 'danger',
        title: '센서리스 AI 예측: 향후 3일 강수 없음 — 위험 심화 전망',
        msg: '기상청 예보 기준 강수 없음. 현재 위험 상태인 검단신도시 조경수구역 A는 예방 관개가 시급합니다.',
        time: '2026-07-19 09:00',
        read: true,
    },
    {
        id: 'AL-226',
        zone: 'Z02',
        level: 'caution',
        title: '세종 다정동 가로수길 수분율 주의(55%)',
        msg: '이팝나무 128주 대상 구역, 정기 관개 주기 도래 3일 전입니다.',
        time: '2026-07-18 16:30',
        read: true,
    },
    {
        id: 'AL-225',
        zone: 'Z10',
        level: 'caution',
        title: '부천 대장신도시 조경수구역 C 정기 점검 예정',
        msg: '소나무·잣나무 74주 대상, 다음주 정기 관개 일정이 예정되어 있습니다.',
        time: '2026-07-18 10:15',
        read: true,
    },
    {
        id: 'AL-224',
        zone: 'Z01',
        level: 'caution',
        title: '위례 신도시 중앙공원 센서 배터리 부족 경고',
        msg: '토양수분센서 SNS-M-014 배터리 잔량 12%. 현장 점검 시 교체가 필요합니다.',
        time: '2026-07-17 11:48',
        read: true,
    },
];

export const INITIAL_ALERTS: AlertItem[] = RAW_ALERTS.map(a => {
    const z = findZone(a.zone);
    return { ...a, zoneName: z ? z.name : '-' };
});

export interface Worker {
    name: string;
    role: string;
    org: string;
    init: string;
    grad: string;
}

export const WORKERS: Worker[] = [
    {
        name: '김관리자',
        role: '관리자',
        org: 'AIS 스마트수목관리',
        init: '김',
        grad: 'linear-gradient(135deg,#1E8B53,#166840)',
    },
    {
        name: '박현장',
        role: '현장관리자',
        org: '(주)수프로 조경',
        init: '박',
        grad: 'linear-gradient(135deg,#2563EB,#1d4ed8)',
    },
    {
        name: '이담당',
        role: '현장관리자',
        org: '(주)수프로 조경',
        init: '이',
        grad: 'linear-gradient(135deg,#7C3AED,#6d28d9)',
    },
    {
        name: '최현장',
        role: '현장작업자',
        org: '(주)수프로 조경',
        init: '최',
        grad: 'linear-gradient(135deg,#D97706,#B45309)',
    },
];

export type WorkType = '관개' | '전정' | '방제' | '시비' | '배수개선';
export type WorkStatus = 'waiting' | 'progress' | 'done';
export type WorkPriority = 'urgent' | 'high' | 'normal';

export interface WorkOrder {
    id: string;
    zone: string;
    type: WorkType;
    title: string;
    assignee: string;
    due: string;
    status: WorkStatus;
    priority: WorkPriority;
    createdFrom: string | null;
    completedAt?: string;
    result?: string;
    zoneName: string;
}

const RAW_WORKORDERS: Omit<WorkOrder, 'zoneName'>[] = [
    {
        id: 'WO-3051',
        zone: 'Z04',
        type: '관개',
        title: '검단신도시 조경수구역 A 긴급 관개',
        assignee: '박현장',
        due: '2026-07-20',
        status: 'progress',
        priority: 'urgent',
        createdFrom: 'AL-231',
    },
    {
        id: 'WO-3050',
        zone: 'Z08',
        type: '관개',
        title: '고양 향동지구 근린공원 예방 관개',
        assignee: '박현장',
        due: '2026-07-20',
        status: 'waiting',
        priority: 'high',
        createdFrom: 'AL-230',
    },
    {
        id: 'WO-3049',
        zone: 'Z03',
        type: '배수개선',
        title: '동탄2 호수공원 배수로 정비',
        assignee: '이담당',
        due: '2026-07-21',
        status: 'waiting',
        priority: 'high',
        createdFrom: 'AL-229',
    },
    {
        id: 'WO-3048',
        zone: 'Z07',
        type: '관개',
        title: '다산신도시 조경수구역 B 정기 관개',
        assignee: '최현장',
        due: '2026-07-22',
        status: 'waiting',
        priority: 'normal',
        createdFrom: 'AL-228',
    },
    {
        id: 'WO-3047',
        zone: 'Z02',
        type: '관개',
        title: '세종 다정동 가로수길 정기 관개',
        assignee: '최현장',
        due: '2026-07-22',
        status: 'waiting',
        priority: 'normal',
        createdFrom: 'AL-226',
    },
    {
        id: 'WO-3046',
        zone: 'Z01',
        type: '전정',
        title: '위례 중앙공원 여름 전정 작업',
        assignee: '김관리자',
        due: '2026-07-23',
        status: 'waiting',
        priority: 'normal',
        createdFrom: null,
    },
    {
        id: 'WO-3045',
        zone: 'Z09',
        type: '방제',
        title: '아산 탕정 가로수길 병해충 방제',
        assignee: '박현장',
        due: '2026-07-18',
        status: 'done',
        priority: 'normal',
        createdFrom: null,
        completedAt: '2026-07-18 15:20',
        result: '병해충 발견 3주 방제 완료, 수분율 82% 양호',
    },
    {
        id: 'WO-3044',
        zone: 'Z06',
        type: '관개',
        title: '파주 운정3 커뮤니티공원 정기 관개',
        assignee: '이담당',
        due: '2026-07-17',
        status: 'done',
        priority: 'normal',
        createdFrom: null,
        completedAt: '2026-07-17 10:05',
        result: '관개량 1,120L, 관개 후 수분율 71% 회복',
    },
    {
        id: 'WO-3043',
        zone: 'Z05',
        type: '시비',
        title: '하남 미사강변 가로수단지 시비 작업',
        assignee: '최현장',
        due: '2026-07-16',
        status: 'done',
        priority: 'normal',
        createdFrom: null,
        completedAt: '2026-07-16 09:40',
        result: '유기질 비료 시비 완료, 152주 전량 처리',
    },
    {
        id: 'WO-3042',
        zone: 'Z04',
        type: '관개',
        title: '검단신도시 조경수구역 A 1차 관개',
        assignee: '박현장',
        due: '2026-07-14',
        status: 'done',
        priority: 'urgent',
        createdFrom: null,
        completedAt: '2026-07-14 13:15',
        result: '관개량 1,860L, 응급 조치. 이후 재악화로 WO-3051 재발행',
    },
];

export const INITIAL_WORKORDERS: WorkOrder[] = RAW_WORKORDERS.map(w => {
    const z = findZone(w.zone);
    return { ...w, zoneName: z ? z.name : '-' };
});

export interface WorkHistoryItem {
    date: string;
    zone: string;
    type: string;
    worker: string;
    amt: string;
    method: string;
    mins: string;
    before: number;
    after: number;
    note: string;
    zoneName: string;
}

const RAW_WORK_HISTORY: Omit<WorkHistoryItem, 'zoneName'>[] = [
    {
        date: '2026-07-19 08:20',
        zone: 'Z09',
        type: '방제',
        worker: '박현장',
        amt: '-',
        method: '약제살포',
        mins: '55분',
        before: 74,
        after: 82,
        note: '진딧물 3주 발견, 방제 완료',
    },
    {
        date: '2026-07-18 15:20',
        zone: 'Z09',
        type: '방제',
        worker: '박현장',
        amt: '-',
        method: '병해충 점검',
        mins: '40분',
        before: 79,
        after: 82,
        note: '병해충 재발 여부 재점검',
    },
    {
        date: '2026-07-17 10:05',
        zone: 'Z06',
        type: '관개',
        worker: '이담당',
        amt: '1,120L',
        method: '점적관수',
        mins: '65분',
        before: 58,
        after: 71,
        note: '정기 관개, 강수 부족 보완',
    },
    {
        date: '2026-07-16 09:40',
        zone: 'Z05',
        type: '시비',
        worker: '최현장',
        amt: '-',
        method: '유기질 시비',
        mins: '90분',
        before: 63,
        after: 63,
        note: '152주 전량 시비 완료',
    },
    {
        date: '2026-07-14 13:15',
        zone: 'Z04',
        type: '관개',
        worker: '박현장',
        amt: '1,860L',
        method: '긴급 점적관수',
        mins: '80분',
        before: 12,
        after: 34,
        note: '응급 조치, 이후 재악화 관찰됨',
    },
    {
        date: '2026-07-11 09:10',
        zone: 'Z03',
        type: '관개',
        worker: '이담당',
        amt: '2,240L',
        method: '스프링클러',
        mins: '70분',
        before: 24,
        after: 32,
        note: '배수 불량으로 효과 제한적',
    },
    {
        date: '2026-07-09 14:30',
        zone: 'Z08',
        type: '관개',
        worker: '박현장',
        amt: '980L',
        method: '수동관수',
        mins: '50분',
        before: 21,
        after: 26,
        note: '정기 관개 지연분 보완',
    },
];

export const INITIAL_WORK_HISTORY: WorkHistoryItem[] = RAW_WORK_HISTORY.map(
    h => {
        const z = findZone(h.zone);
        return { ...h, zoneName: z ? z.name : '-' };
    },
);

export interface SensorItem {
    name: string;
    id: string;
    ico: string;
    icoRi: string;
    icoClass: string;
    val: string;
    unit: string;
    pct: number;
    color: string;
    zoneName: string;
    online: boolean;
    battery: number;
}

export const SENSORS: SensorItem[] = [
    {
        name: '토양수분센서 #A14',
        id: 'SNS-M-014',
        ico: '💧',
        icoRi: 'ri-drop-line',
        icoClass: 's-ico-blue',
        val: '18',
        unit: '토양 수분율',
        pct: 18,
        color: STATUS_COLOR.danger,
        zoneName: '검단신도시 조경수구역 A',
        online: true,
        battery: 41,
    },
    {
        name: '토양수분센서 #B05',
        id: 'SNS-M-005',
        ico: '💧',
        icoRi: 'ri-drop-line',
        icoClass: 's-ico-green',
        val: '78',
        unit: '토양 수분율',
        pct: 78,
        color: STATUS_COLOR.good,
        zoneName: '위례 신도시 중앙공원',
        online: true,
        battery: 12,
    },
    {
        name: '기상관측센서 #01',
        id: 'SNS-W-001',
        ico: '🌡️',
        icoRi: 'ri-thermometer-line',
        icoClass: 's-ico-blue',
        val: '29',
        unit: '외기 온도',
        pct: 58,
        color: STATUS_COLOR.caution,
        zoneName: '전체 구역 대표',
        online: true,
        battery: 88,
    },
    {
        name: '토양EC센서 #C02',
        id: 'SNS-EC-002',
        ico: '⚡',
        icoRi: 'ri-flashlight-line',
        icoClass: 's-ico-green',
        val: '1.4',
        unit: '전기전도도',
        pct: 28,
        color: '#4A6B4A',
        zoneName: '화성 동탄2 호수공원',
        online: true,
        battery: 65,
    },
    {
        name: '토양수분센서 #D03',
        id: 'SNS-M-003',
        ico: '⚠️',
        icoRi: 'ri-error-warning-line',
        icoClass: 's-ico-red',
        val: '--',
        unit: '연결 오류',
        pct: 0,
        color: STATUS_COLOR.danger,
        zoneName: '인천 검단신도시',
        online: false,
        battery: 0,
    },
    {
        name: '강수량센서 #01',
        id: 'SNS-R-001',
        ico: '🌧️',
        icoRi: 'ri-showers-line',
        icoClass: 's-ico-blue',
        val: '3.2',
        unit: '주간 누적 강수',
        pct: 16,
        color: STATUS_COLOR.caution,
        zoneName: '전체 구역',
        online: true,
        battery: 74,
    },
];

export const TYPE_ICON: Record<string, string> = {
    관개: '💧',
    전정: '✂️',
    방제: '🧪',
    시비: '🌱',
    배수개선: '🚧',
};
